<template>
  <div class="intro-speakers-player">
    <audio ref="audioPlayer" class="hidden"></audio>
  </div>
</template>

<script>
import API from "@/api";

export default {
  name: 'IntroSpeakersPlayer',

  props: {
    isResponseInProgress: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      introMessage: "Thank you for your request. We're creating your tailored content experience - where ideas transform into conversations that leave lasting impressions.",
      outroMessage: "Your content experience is ready.",
      waitingMessage: "Almost there! Remember, specificity sparks brilliance - the more details you provide, the more precisely we can bring your vision to life. Currently synthesizing your input into something extraordinary... just a moment longer.",
      // Updated speakers array with real user use cases
      speakers: [
        {
          name: "Sam Kokoro",
          role: "Host, UnStruct.AI",
          message: "UnStruct.AI rewires how information connects with human minds. Transform dense research papers into riveting discussions, complex data into accessible insights, or abstract concepts into tangible conversations that stick in memory long after listening stops."
        },
        {
          name: "Jordan Singh",
          role: "Research Scientist",
          message: "Revolutionize your professional brand by translating static LinkedIn profiles into dynamic narratives. Convert client testimonials or recommendations into immersive case studies. Turn quarterly reports into strategic dialogues. We're not just changing format - we're rewiring how information resonates."
        },
        {
          name: "Alex Chen",
          role: "Senior Analyst",
          message: "In a world drowning in content, breakthrough brands need neural resonance. Our platform transforms marketing copy into conversations that trigger precisely the right neurons. We don't just tell stories - we create experiences that bypass cognitive filters and establish lasting neural pathways."
        },
        {
          name: "Morgan Patel",
          role: "Lead Researcher",
          message: "Imagine your startup pitch evolving into a compelling dialogue between visionaries, your academic paper becoming an electrifying debate, or your product documentation transforming into interactive learning. UnStruct.AI bridges the gap between information delivery and neural integration."
        }
      ],
      speakerVoices: [
        "bf_emma+bf_v0isabella+af_heart+af_nicole",  // male
        "bf_emma+bf_v0isabella+af_heart+af_nicole",
        "bf_emma+bf_v0isabella+af_heart+af_nicole",
        "bf_emma+bf_v0isabella+af_heart+af_nicole"
      ],
      voiceSpeed: 1.33,
      voiceProvider: 'local',
      audioCache: new Map(),
      currentSpeakerIndex: 0,
      isPlaying: false,
      backgroundMusic: null,
      introFinished: false, // Track when intro is complete
      audioContext: null,
      gainNode: null,
      playingOutro: false,
      playingWaitingMessage: false, // Track if waiting message is playing
      currentAudioUrl: null,
    };
  },

  watch: {
    isResponseInProgress: {
      immediate: true,
      handler(newValue, oldValue) {
        if (newValue) {
          // Response started - begin intro
          this.startIntro();
          this.introFinished = false; // Reset the finished flag when starting
        } else if (oldValue) {
          // Response finished - play outro if we were playing
          if (this.isPlaying) {
            // Only play outro if we have gone through at least one speaker
            if (this.currentSpeakerIndex >= 0) {
              // Just play the outro - don't emit intro-finished here
              // We'll emit that only when the outro is DONE playing
              this.speakOutroMessage();
            } else {
              // In this edge case where we never played any speakers
              // Just clean up, don't autoplay
              this.cleanupAudio();
              this.isPlaying = false;
              // We intentionally don't emit intro-finished here
              this.introFinished = true;
            }
          } else {
            // This is the fix: If we're not playing, just clean up but don't emit
            if (!this.introFinished) {
              this.cleanupAudio();
              this.isPlaying = false;
              this.introFinished = true;
              // We don't auto-play in this case - user will need to manually play
            }
          }
        }
      }
    }
  },
  mounted() {
    // Initialize a single audio element for all audio playback
    this.$refs.audioPlayer.addEventListener('ended', this.handleAudioEnded);
  },

  beforeDestroy() {
    this.cleanupAudio();
    if (this.$refs.audioPlayer) {
      this.$refs.audioPlayer.removeEventListener('ended', this.handleAudioEnded);
    }
    this.stopBackgroundMusic();
  },

  methods: {
    // New method to shuffle speakers array
    shuffleSpeakers() {
      // Create a copy of the original speakers array to shuffle
      const speakersToShuffle = [...this.speakers];
      const voicesToShuffle = [...this.speakerVoices];

      // Fisher-Yates shuffle algorithm
      for (let i = speakersToShuffle.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [speakersToShuffle[i], speakersToShuffle[j]] = [speakersToShuffle[j], speakersToShuffle[i]];

        // Also shuffle the corresponding voices to keep them matched
        [voicesToShuffle[i], voicesToShuffle[j]] = [voicesToShuffle[j], voicesToShuffle[i]];
      }

      // Update the speakers and voices arrays with the shuffled versions
      this.speakers = speakersToShuffle;
      this.speakerVoices = voicesToShuffle;

      // Clear audio cache since the indices have changed
      this.audioCache.clear();
    },

    startIntro() {
      // Shuffle the speakers before starting the intro
      this.shuffleSpeakers();

      this.currentSpeakerIndex = -1; // Start with intro message
      this.isPlaying = true;
      this.playingOutro = false;
      this.playingWaitingMessage = false;
      this.speakIntroMessage();
      this.playBackgroundMusic();
    },

    async speakIntroMessage() {
      if (!this.isPlaying) return;

      try {
        const audioData = await this.fetchAudioForText(this.introMessage, "bf_emma+bf_v0isabella+af_heart+af_nicole");
        await this.playAudioData(audioData);
      } catch (error) {
        console.error('Error playing intro message:', error);
        this.currentSpeakerIndex = 0;
        this.speakCurrentMessage();
      }
    },

    async speakOutroMessage() {
      // If outro is already in progress or we're not playing, don't start another
      if (!this.isPlaying || this.playingOutro) {
        return;
      }

      // Set the flag immediately
      this.playingOutro = true;

      try {
        const audioData = await this.fetchAudioForText(this.outroMessage, "bf_emma+bf_v0isabella+af_heart+af_nicole");
        await this.playAudioData(audioData);
        // Do NOT emit intro-finished here
        // It will be emitted when the audio finishes playing via handleAudioEnded
      } catch (error) {
        console.error('Error playing outro message:', error);
        this.isPlaying = false;
        this.playingOutro = false;

        // In case of error, wait a moment then emit
        setTimeout(() => {
          if (!this.introFinished) {
            this.$emit('intro-finished');
            this.introFinished = true;
          }
        }, 300);
      }
    },

    async speakWaitingMessage() {
      // If waiting message is already in progress or we're not playing, don't start another
      if (!this.isPlaying || this.playingWaitingMessage) {
        return;
      }

      // Set the flag immediately
      this.playingWaitingMessage = true;

      try {
        const audioData = await this.fetchAudioForText(this.waitingMessage, "bf_emma+bf_v0isabella+af_heart+af_nicole");
        await this.playAudioData(audioData);
      } catch (error) {
        console.error('Error playing waiting message:', error);
        this.playingWaitingMessage = false;

        // If response is ready after error, play outro
        if (!this.isResponseInProgress && this.isPlaying) {
          this.speakOutroMessage();
        }
      }
    },

    // New method to play audio using the single audio element
    async playAudioData(audioData) {
      try {
        // Create a blob from the audio data
        const audioBlob = new Blob([audioData], { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(audioBlob);

        // Store the URL to revoke it later
        this.currentAudioUrl = audioUrl;

        // Set the source of our single audio element
        const audioElement = this.$refs.audioPlayer;
        audioElement.src = audioUrl;

        // Play the audio
        return audioElement.play();
      } catch (error) {
        console.error('Error playing audio data:', error);
        throw error;
      }
    },

    handleAudioEnded() {
      // Revoke the object URL to free memory
      if (this.currentAudioUrl) {
        URL.revokeObjectURL(this.currentAudioUrl);
        this.currentAudioUrl = null;
      }

      // Check if this was the outro message
      if (this.playingOutro) {
        this.playingOutro = false;
        this.isPlaying = false;

        // Make sure background music stops
        this.stopBackgroundMusic();

        // THIS IS THE ONLY PLACE we should emit intro-finished
        // And we add a small delay to ensure clean transition
        setTimeout(() => {
          if (!this.introFinished) {
            this.$emit('intro-finished');
            this.introFinished = true;
          }
        }, 300); // Small delay to ensure clean transition

        return;
      }

      // Check if this was the waiting message
      if (this.playingWaitingMessage) {
        this.playingWaitingMessage = false;

        // If response is ready after waiting message, play outro
        if (!this.isResponseInProgress && this.isPlaying) {
          this.speakOutroMessage();
        } else if (this.isResponseInProgress && this.isPlaying) {
          // Response still in progress, continue with another random speaker
          this.selectRandomSpeaker();
          this.speakCurrentMessage();
        }
        return;
      }

      // Otherwise, move to the next speaker or message
      if (this.currentSpeakerIndex === -1) {
        // Intro message finished, move to first speaker
        this.currentSpeakerIndex = 0;
        this.speakCurrentMessage();
      } else {
        // Speaker message finished, move to next speaker
        this.moveToNextSpeaker();
      }
    },

    // Method to select a random speaker instead of sequential
    selectRandomSpeaker() {
      // Select a random speaker index
      const availableSpeakers = this.speakers.length;
      const lastIndex = this.currentSpeakerIndex;

      // Make sure we don't repeat the same speaker consecutively
      let newIndex;
      do {
        newIndex = Math.floor(Math.random() * availableSpeakers);
      } while (newIndex === lastIndex && availableSpeakers > 1);

      this.currentSpeakerIndex = newIndex;
    },

    stopIntro() {
      this.isPlaying = false;
      this.stopAllAudio();

      // Mark as finished but don't emit the event here
      // The event should only be emitted after the outro has fully played
      this.introFinished = true;
    },

    playBackgroundMusic() {
      if (this.backgroundMusic) {
        this.backgroundMusic.pause();
      }

      try {
        // Create audio context
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        this.audioContext = new AudioContext();

        // Create gain node for volume
        this.gainNode = this.audioContext.createGain();
        this.gainNode.connect(this.audioContext.destination);
        this.gainNode.gain.value = 0.05; // Very low volume for background

        // Create and play audio
        this.backgroundMusic = new Audio(`https://${window.location.host}/static/Odyssey-Eveningland.mp3`);
        const source = this.audioContext.createMediaElementSource(this.backgroundMusic);
        source.connect(this.gainNode);

        this.backgroundMusic.loop = true;
        this.backgroundMusic.play().catch(error => {
        console.error('Failed to play background music:', error);
        });
      } catch (error) {
        console.warn('Error setting up background music:', error);

        // Fallback to direct volume control if audio context fails
        try {
        this.backgroundMusic = new Audio(`https://${window.location.host}/static/Odyssey-Eveningland.mp3`);
        this.backgroundMusic.volume = 0.01;
        this.backgroundMusic.loop = true;
        this.backgroundMusic.play().catch(e => console.warn('Fallback music failed:', e));
        } catch (e) {
        console.warn('Fallback music setup failed:', e);
        }
      }
    },

    async speakCurrentMessage() {
      if (!this.isPlaying || this.currentSpeakerIndex >= this.speakers.length) {
        return;
      }

      try {
        // Get the audio data for the current speaker
        const audioData = await this.fetchAudioForSpeaker(this.currentSpeakerIndex);

        // Play it using our single audio element
        await this.playAudioData(audioData);

        // Pre-fetch next speaker audio - randomize for better performance
        this.prefetchRandomSpeakers();
      } catch (error) {
        console.error('Error playing speech:', error);
        this.moveToNextSpeaker();
      }
    },

    // Method to prefetch random speakers to improve responsiveness
    prefetchRandomSpeakers() {
      // Get list of indices we haven't cached yet
      const uncachedIndices = [];
      for (let i = 0; i < this.speakers.length; i++) {
        if (!this.audioCache.has(i)) {
          uncachedIndices.push(i);
        }
      }

      // If there are uncached speakers, fetch one at random
      if (uncachedIndices.length > 0) {
        const randomIndex = uncachedIndices[Math.floor(Math.random() * uncachedIndices.length)];
        this.fetchAudioForSpeaker(randomIndex).catch(error => {
          console.warn(`Pre-fetch failed for speaker ${randomIndex}:`, error);
        });
      }
    },

    async fetchAudioForSpeaker(speakerIndex) {
      // Check cache first
      if (this.audioCache.has(speakerIndex)) {
        return this.audioCache.get(speakerIndex);
      }

      const speaker = this.speakers[speakerIndex];
      const text = speaker.message;
      const voice = this.speakerVoices[speakerIndex];

      const audioData = await this.fetchAudioForText(text, voice);

      // Cache the result
      this.audioCache.set(speakerIndex, audioData);

      return audioData;
    },

    async fetchAudioForText(text, voice) {
      const token = localStorage.getItem("token");
      const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");
      const url = token ? '/tts' : `/tts/anonymous/${unstruct_anonymous_key}`;

      const response = await API.post(url, {
        text,
        provider: this.voiceProvider,
        voice: voice,
        speed: this.voiceSpeed
      }, {
        responseType: 'arraybuffer',
        headers: {
          'Accept': 'audio/mpeg',
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    },

    moveToNextSpeaker() {
      // For true randomization, use selectRandomSpeaker instead of sequential
      this.selectRandomSpeaker();

      // Alternative: sequential navigation through shuffled array
      // this.currentSpeakerIndex++;

      if (this.currentSpeakerIndex < this.speakers.length && this.isPlaying) {
        this.speakCurrentMessage();
      } else if (this.isPlaying) {
        // All speakers have finished
        if (this.isResponseInProgress) {
          // Response is still in progress, play a waiting message
          this.speakWaitingMessage();
        } else {
          // Response is already done, play outro message
          this.speakOutroMessage();
        }
      }
    },

    stopAllAudio() {
      // Stop the main audio element
      if (this.$refs.audioPlayer) {
        this.$refs.audioPlayer.pause();
        if (this.$refs.audioPlayer.src) {
          try {
            URL.revokeObjectURL(this.$refs.audioPlayer.src);
          } catch (e) {
            // Ignore errors from revoking URLs
          }
          this.$refs.audioPlayer.removeAttribute('src');
        }
      }

      // Stop background music properly
      this.stopBackgroundMusic();
    },

    stopBackgroundMusic() {
      // Stop background music
      if (this.backgroundMusic) {
        this.backgroundMusic.pause();
        // Set the source to empty to fully release it
        if (this.backgroundMusic.src) {
          this.backgroundMusic.src = '';
        }
        this.backgroundMusic = null;
      }

      // Clean up audio context and nodes
      if (this.gainNode) {
        try {
          this.gainNode.disconnect();
        } catch (e) {
          // Ignore disconnect errors
        }
        this.gainNode = null;
      }

      if (this.audioContext && this.audioContext.state !== 'closed') {
        try {
          this.audioContext.close();
        } catch (e) {
          console.warn('Error closing audio context:', e);
        }
        this.audioContext = null;
      }
    },

    cleanupAudio() {
      this.stopAllAudio();

      // Clear audio cache
      this.audioCache.forEach((_, key) => {
        this.audioCache.delete(key);
      });
    }
  }
};
</script>

<style scoped>
.hidden {
  display: none;
}
</style>
